'use client';

import { formatPrice, TTagSeverity, TCard, cn } from 'types';
import { Image } from '@crystallize/reactjs-components';
import { Tag, MessageBar, Icon, IconType, CheckBox } from '..';

export const Card = ({
  id,
  title,
  tagValue,
  displayTag,
  className,
  customPriceTag,
  included,
  price,
  selected,
  isDisabled = false,
  bosSwatch,
  alertMessage,
  bosDescription,
  tagModal,
  disableTag,
  isCardLocked,
  updateSelected,
  onTagClick,
  onDisableClick,
}: TCard) => {
  return (
    <div
      className={cn(
        'w-fit h-fit flex items-center relative p-2 rounded-lg border m-px',
        selected ? 'outline outline-concrete border-concrete rounded-lg bg-lightGrey' : 'border-transparent',
        'hover:bg-lightGrey hover:border-neutralPlus',
        'focus:bg-lightGrey focus:border-black',
        isDisabled ? 'cursor-initial text-neutralPlus hover:bg-transparent hover:border-transparent' : 'cursor-pointer',
        selected && isDisabled ? 'outline-neutralPlus border-neutralPlus' : '',
        className
      )}
      onClick={() => {
        !isCardLocked && isDisabled ? onDisableClick() : updateSelected(!selected ? id : '');
      }}
    >
      <div className={cn('flex', bosSwatch ? 'gap-4' : 'gap-2')}>
        {bosSwatch ? (
          <div className={cn('w-20 h-20 flex items-center justify-center rounded-sm', isDisabled ? 'opacity-50' : '')}>
            <Image {...bosSwatch} className="[&_img]:rounded-sm h-full [&_img]:h-full [&_img]:w-20" />
            {selected && (
              <CheckBox
                id={id}
                checked={selected}
                className="absolute top-px left-px border-2 border-white rounded-sm [&_input]:m-0!"
                readonly
                disabled={isDisabled}
              />
            )}
          </div>
        ) : (
          <>
            {isDisabled && isCardLocked ? (
              <>
                <div className="flex w-6 h-6 items-center justify-center">
                  <Icon icon={IconType.NewNextLock} width={24} height={24} />
                </div>
              </>
            ) : (
              <CheckBox id={id} checked={selected} className="-mt-[4px]" readonly disabled={isDisabled} />
            )}
          </>
        )}

        <div className="flex gap-1 pr-2">
          <div className="flex flex-col">
            <p className="text-base/[130%] font-extrabold">{title || ''}</p>
            <div className="flex flex-col w-fit">
              {displayTag && tagValue && (
                <div
                  className="cursor-pointer w-fit"
                  onClick={
                    !disableTag && !isDisabled
                      ? (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onTagClick ? onTagClick(id) : null;
                        }
                      : null
                  }
                >
                  <Tag
                    severity={disableTag ? TTagSeverity.NEUTRAL : TTagSeverity.INFORMATION}
                    className={cn('cursor-pointer', disableTag ? 'hover:enabled:!border-neutralPlus' : '')}
                  >
                    <span className="font-semibold flex items-center justify-center text-sm py-px">{tagValue}</span>
                  </Tag>
                </div>
              )}
              {price && <div className="text-base/[175%] font-semibold">{formatPrice(price)}</div>}
              {customPriceTag && !included && !price && (
                <span className="text-base/[175%] font-semibold">{customPriceTag}</span>
              )}
              {bosDescription?.length > 0 && (
                <div className="flex flex-col">
                  {bosDescription?.map((item, key) => (
                    <span className="text-base/[150%] font-semibold" key={key}>
                      {item.title}: {item.value}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {alertMessage?.message && (
              <MessageBar severity={alertMessage?.typeOfAlert} className="tw-small">
                {alertMessage.message}
              </MessageBar>
            )}
          </div>
          {alertMessage?.showIcon && (
            <div
              className="h-fit"
              onClick={
                alertMessage?.onIconClick
                  ? (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      alertMessage.onIconClick();
                    }
                  : null
              }
            >
              <Icon icon={IconType.NewNextWarningSolid} height={24} width={24} fill="var(--color-oker)" />
            </div>
          )}
        </div>
      </div>
      {tagModal?.isOpen ? <div className="z-[10] absolute top-10 left-14">{tagModal?.modalChildComponent}</div> : null}
    </div>
  );
};
